// src/features/rooms/components/UploadRoomDrawingDialog.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useRoomFiles } from '../hooks/useRoomFiles';
import { useRooms } from '../hooks/useRooms';
import { FileDropZone, useFilePicker, useUploadQueue, UploadProgressList } from '../../files/upload';

/**
 * Uploads a drawing for one room. The file is uploaded as soon as it's dropped
 * or picked, set as the room's drawing, and the dialog closes.
 */
export default function UploadRoomDrawingDialog({ open, onClose, roomId, roomName, onSuccess }) {
  const { t } = useTranslation(['rooms', 'files', 'common']);
  const { uploadRoomFile } = useRoomFiles();
  const { updateRoom } = useRooms();
  const [error, setError] = useState(null);

  const uploads = useUploadQueue({
    upload: (file, { onUploadProgress }) => uploadRoomFile(file, roomName, { onUploadProgress }),
    onUploaded: async (result) => {
      const fileId = result.data?.data?.file_id;
      if (!fileId) {
        setError(t('rooms:errors.file_upload_no_id'));
        return;
      }
      const updateResult = await updateRoom(roomId, { room_drawing_file_id: fileId });
      if (updateResult?.success) {
        onSuccess?.(result);
        onClose();
      } else {
        setError(updateResult?.error || t('common:errors.generic'));
      }
    },
  });
  const addFile = (files) => {
    setError(null);
    uploads.enqueue(files.slice(0, 1));
  };
  const picker = useFilePicker({ onFiles: addFile, multiple: false });

  const handleClose = () => {
    if (uploads.busy) return;
    setError(null);
    uploads.clearFinished();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('rooms:upload_room_drawing')}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <FileDropZone onFiles={addFile} multiple={false} disabled={uploads.busy}>
            <Box
              component="button"
              type="button"
              onClick={picker.open}
              disabled={uploads.busy}
              sx={{
                width: '100%',
                p: 4,
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                color: 'inherit',
                font: 'inherit',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover, &:focus-visible': { borderColor: 'primary.main', bgcolor: 'action.hover', outline: 'none' },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 56, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                {t('files:drag_drop_file')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('files:or_click_to_browse')}
              </Typography>
            </Box>
          </FileDropZone>
          {picker.input}
          <UploadProgressList items={uploads.items} onDismiss={uploads.dismiss} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploads.busy}>
          {t('common:buttons.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
